import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Injectable,
  TemplateRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IcamIconComponent } from '../icam-icon/icam-icon.component';
import {
  NgbCalendar,
  NgbDate,
  NgbDateStruct,
  NgbDatepickerModule,
  NgbDatepickerI18n
} from '@ng-bootstrap/ng-bootstrap';
import { format, parse } from 'date-fns';

const DATE_FORMAT = 'dd/MM/yyyy';
const API_DATE_FORMAT = 'yyyy-MM-dd';

export interface DatepickerChangeEvent {
  name: string;
  selectedValue: string;
  text: string;
}

@Injectable()
export class IcamDatepickerI18n extends NgbDatepickerI18n {
  private _translate = inject(TranslateService);

  getWeekdayLabel(weekday: number): string {
    const locale = this._translate.currentLang || 'it';
    const date = new Date(2021, 5, 13 + weekday);
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date).toLowerCase();
  }

  getWeekdayShortName(weekday: number): string {
    return this.getWeekdayLabel(weekday);
  }

  getMonthShortName(month: number): string {
    return this.getMonthFullName(month);
  }

  getMonthFullName(month: number): string {
    const date = new Date(2021, month - 1, 1);
    const locale = this._translate.currentLang || 'it';
    return new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}

@Component({
  selector: 'app-icam-datepicker',
  templateUrl: './icam-datepicker.component.html',
  styleUrl: './icam-datepicker.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, IcamIconComponent, NgbDatepickerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NgbDatepickerI18n, useClass: IcamDatepickerI18n }]
})
export class IcamDatepickerComponent implements OnInit, OnChanges {
  private _calendar = inject(NgbCalendar);
  private _cdr = inject(ChangeDetectorRef);

  @Input() name: 'date' | 'custom_period' = 'date';
  @Input() isLight = true;
  @Input() disabled = false;
  @Input() initialValue?: string;
  @Input() placeholder?: string;
  @Input() minDate?: NgbDateStruct;
  @Input() maxDate?: NgbDateStruct;

  @Output() dateChange = new EventEmitter<DatepickerChangeEvent>();

  @ViewChild('datepicker') datepicker!: any;
  @ViewChild('singleDatepicker') singleDatepicker!: any;
  @ViewChild('footerTemplate') footerTemplate!: TemplateRef<any>;

  isDatePickerOpen = false;
  private isToggling = false;

  get isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate;
  _toDate: NgbDate | null = null;

  private _startDate!: Date;
  private _endDate!: Date;
  private _selectedDate!: Date;

  disabledBtn = true;
  value?: string = '';

  constructor() {
    const today = this._calendar.getToday();
    this.fromDate = this._calendar.getPrev(today, 'd', 7);
    this._toDate = today;
  }

  ngOnInit() {
    this.initMinMaxDates();
    this.initInitialValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialValue'] && !changes['initialValue'].firstChange) {
      this.handleInitialValueChange(changes['initialValue'].currentValue);
    }
    if (changes['minDate'] || changes['maxDate']) {
      this.initMinMaxDates();
      this._cdr.markForCheck();
    }
  }

  private initMinMaxDates() {
    if (!this.minDate) {
      this.minDate = {
        year: 2015,
        month: 1,
        day: 1
      };
    }

    if (!this.maxDate) {
      this.maxDate = {
        year: 2045,
        month: 12,
        day: 31
      };
    }
  }

  private initInitialValue() {
    if (!this.initialValue) return;

    this.value = this.initialValue;

    if (this.name === 'custom_period') {
      this.parseAndSetRange(this.initialValue);
    } else {
      this.parseAndSetDate(this.initialValue);
    }
  }

  private handleInitialValueChange(newValue: string) {
    this.value = newValue;

    if (newValue) {
      if (this.name === 'custom_period') {
        this.parseAndSetRange(newValue);
      } else {
        this.parseAndSetDate(newValue);
      }
    }
    this._cdr.markForCheck();
  }

  private parseAndSetRange(rangeStr: string) {
    const dates = rangeStr.split(' - ');
    if (dates.length !== 2) return;

    try {
      const start = parse(dates[0], DATE_FORMAT, new Date());
      const end = parse(dates[1], DATE_FORMAT, new Date());

      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        this.fromDate = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
        this._toDate = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());
        this.disabledBtn = false;
      }
    } catch (_err) {
      console.warn('Could not parse initial range value', _err);
    }
  }

  private parseAndSetDate(dateStr: string) {
    try {
      const date = parse(dateStr, DATE_FORMAT, new Date());
      if (!isNaN(date.getTime())) {
        this.disabledBtn = false;
      }
    } catch (_err) {
      console.warn('Could not parse initial date value', _err);
    }
  }

  onDateSelection(date?: NgbDate) {
    if (!date) return;

    if (this.name === 'date') {
      this.handleSingleDateSelection(date);
    } else if (this.name === 'custom_period') {
      this.handleRangeSelection(date);
    }
    this._cdr.markForCheck();
  }

  private handleSingleDateSelection(date: NgbDate) {
    const { year, month, day } = date;
    this._selectedDate = new Date(year, month - 1, day);

    const formattedDate = format(this._selectedDate, DATE_FORMAT);
    const dateValue = format(this._selectedDate, API_DATE_FORMAT);

    this.value = formattedDate;
    this.disabledBtn = false;

    this.dateChange.emit({
      name: 'date',
      selectedValue: dateValue,
      text: formattedDate
    });
  }

  private handleRangeSelection(date: NgbDate) {
    if (!this.fromDate && !this._toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this._toDate && date.after(this.fromDate)) {
      this._toDate = date;
    } else {
      this._toDate = null;
      this.fromDate = date;
    }

    if (this.fromDate && this._toDate) {
      this.disabledBtn = false;

      const { year: sY, month: sM, day: sD } = this.fromDate;
      const { year: eY, month: eM, day: eD } = this._toDate;

      this._startDate = new Date(sY, sM - 1, sD);
      this._endDate = new Date(eY, eM - 1, eD);

      const displayText = `${format(this._startDate, DATE_FORMAT)} - ${format(this._endDate, DATE_FORMAT)}`;
      const isoValue = `${format(this._endDate, API_DATE_FORMAT)}T23:59:59.999Z - ${format(
        this._startDate,
        API_DATE_FORMAT
      )}T00:00:00Z`;

      this.value = displayText;

      this.dateChange.emit({
        name: 'date_period',
        selectedValue: isoValue,
        text: displayText
      });
    }
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate && !this._toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this._toDate && date.after(this.fromDate) && date.before(this._toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this._toDate && date.equals(this._toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  closeDatePicker() {
    this.isDatePickerOpen = false;
    this.datepicker?.close();
    this.singleDatepicker?.close();
    this._cdr.markForCheck();
  }

  triggerOpen(datepicker: any) {
    if (this.disabled || this.isToggling) return;

    if (this.isDatePickerOpen) {
      this.closeDatePicker();
      datepicker.close();
    } else {
      this.isDatePickerOpen = true;
      setTimeout(() => {
        datepicker.open();
        this._cdr.markForCheck();
      }, 0);
    }

    this.isToggling = true;
    setTimeout(() => {
      this.isToggling = false;
      this._cdr.markForCheck();
    }, 100);
  }

  clearDateSelection(): void {
    if (this.disabled) return;
    this.value = '';
    this.fromDate = this._calendar.getPrev(this._calendar.getToday(), 'd', 7);
    this._toDate = null;
    this.disabledBtn = true;

    this.dateChange.emit({
      name: this.name === 'date' ? 'date' : 'date_period',
      selectedValue: '',
      text: ''
    });
    this._cdr.markForCheck();
  }

  public setDateRange(startDate: string, endDate: string, emitEvent = false): void {
    if (!startDate || !endDate) return;

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      this.fromDate = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
      this._toDate = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());

      const displayText = `${format(start, DATE_FORMAT)} - ${format(end, DATE_FORMAT)}`;

      this.value = displayText;
      this.disabledBtn = false;

      this._startDate = start;
      this._endDate = end;

      if (emitEvent) {
        const isoValue = `${format(end, API_DATE_FORMAT)}T23:59:59.999Z - ${format(start, API_DATE_FORMAT)}T00:00:00Z`;

        this.dateChange.emit({
          name: 'date_period',
          selectedValue: isoValue,
          text: displayText
        });
      }
      this._cdr.markForCheck();
    } catch (error) {
      console.error('Error setting date range:', error);
    }
  }
}
